import { createFactory } from '@withease/factories'
import { createForm } from 'effector-forms'
import { sample } from 'effector'
import { type PersonSuggestion } from '@mtsdengi/ui-kit'
import { isCyrillicFullName, validateEmail } from '@shared/utils'

export const MIN_USER_AGE = 18
export const MAX_USER_AGE = 70

export const createIdentityClientFormFactory = createFactory(() => {
  const identityClientForm = createForm({
    fields: {
      phoneNumber: {
        init: '',
        rules: [
          {
            name: 'required',
            validator: (phoneNumber) => ({
              isValid: Boolean(phoneNumber),
              errorText: 'Обязательное поле',
            }),
          },
          {
            name: 'valid',
            validator: (phoneNumber) => ({
              isValid: phoneNumber.replace(/[ \-+]/g, '').length > 10,
              errorText: 'Введите верный номер телефона',
            }),
          },
        ],
      },
      clientFio: {
        init: '',
        rules: [
          {
            name: 'required',
            validator: (fio) => ({
              isValid: fio.trim().length > 0,
              errorText: 'Введите ФИО полностью',
            }),
          },
          {
            name: 'single-cyrillic',
            validator: (fio) => ({
              isValid: isCyrillicFullName(fio),
              errorText: 'Используйте только кириллицу',
            }),
          },
          {
            name: 'is-full',
            validator: (fio) => ({
              isValid: [2, 3, 4].includes(fio.split(' ').filter(Boolean).length),
              errorText: 'Введите ФИО полностью',
            }),
          },
        ],
      },
      clientFioSuggest: {
        init: {} as PersonSuggestion,
      },
      email: {
        init: '',
        rules: [
          {
            name: 'required',
            validator: (email) => ({
              isValid: email.length === 0 || validateEmail(email),
              errorText: 'Введите верный электронный адрес',
            }),
          },
        ],
      },
      // Обязательное поле
      birthDate: {
        init: null as Date | Date[] | string | number | null,
        rules: [
          {
            name: 'required',
            validator: (birthDate) => ({
              isValid: Boolean(birthDate),
              errorText: 'Обязательное поле',
            }),
          },
          {
            name: 'minClientAge',
            validator: (birthDate) => ({
              isValid:
                birthDate <
                new Date(new Date().getFullYear() - MIN_USER_AGE, new Date().getMonth(), new Date().getDate()),
              errorText: `Возраст клиента должен быть не менее ${MIN_USER_AGE} лет`,
            }),
          },
          {
            name: 'maxClientAge',
            validator: (birthDate) => ({
              isValid:
                birthDate >
                new Date(new Date().getFullYear() - MAX_USER_AGE, new Date().getMonth(), new Date().getDate()),
              errorText: `Возраст клиента должен быть не более ${MAX_USER_AGE} лет`,
            }),
          },
        ],
      },
      checkAllAgreements: {
        init: true,
      },
    },
    validateOn: ['submit', 'blur'],
  })

  // При изменении ФИО, очистить suggest дадаты.
  sample({
    clock: identityClientForm.fields.clientFio.onChange,
    target: identityClientForm.fields.clientFioSuggest.reset,
  })

  // Если было выбрано ФИО, заполнить отображаемое значение в текстовом поле и пол сотрудника.
  sample({
    clock: identityClientForm.fields.clientFioSuggest.onChange,
    fn: ({ full, gender }) => ({
      clientFio: full,
      gender,
    }),
    target: identityClientForm.set,
  })

  const $notPatronimyc = identityClientForm.fields.clientFioSuggest.$value.map((clientFioSuggest) => {
    if (Object.keys(clientFioSuggest).length === 0) {
      return null
    }

    return !clientFioSuggest.patronymic
  })

  return {
    identityClientForm,

    stores: {
      $notPatronimyc,
    },

    events: {},
  }
})
